import { Fetcher, Found, search } from './search';

/**
 * <ask-codey> (ADR-0044): the "Ask Codey…" field for the header of every page, in any framework or none.
 *
 * Typing two letters or more (after a short pause) searches glossary terms, lessons and cheat sheets and opens a panel
 * of what it found, grouped, each a link; Premium's guided answer comes first. Escape or a click outside closes it,
 * and "/" anywhere on the page puts the cursor in it. With no `signed-in` attribute, the panel also says registration
 * is free. Clicking a result dispatches a cancelable `ask-codey-navigate` event ({ url }) that bubbles out of the
 * page: an app that handles it (and calls preventDefault) routes without a reload; otherwise the link just works.
 * Its styles live in its shadow root and use the design tokens (--color-*, --font-*) the page already has.
 */
export class AskCodey extends HTMLElement {
  /** How long it waits after the last key before searching. */
  static debounceMs = 250;
  /** Results per kind. */
  static limit = 5;

  /** How it asks the services; the tests hand in their own. */
  fetcher: Fetcher = (url, init) => fetch(url, init);

  private readonly root = this.attachShadow({ mode: 'open' });
  private input!: HTMLInputElement;
  private panel!: HTMLElement;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private asked = 0;

  private readonly onDocumentClick = (e: Event) => {
    if (!e.composedPath().includes(this)) this.close();
  };
  private readonly onDocumentKey = (e: KeyboardEvent) => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const target = e.composedPath()[0] as HTMLElement | undefined;
    if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
    e.preventDefault();
    this.input.focus();
  };

  connectedCallback(): void {
    if (!this.input) this.render();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKey);
  }

  disconnectedCallback(): void {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKey);
    if (this.timer) clearTimeout(this.timer);
  }

  private render(): void {
    const codey = this.getAttribute('codey-src') ?? '/design-system/assets/codey-front.png';
    this.root.innerHTML = `
      <style>${STYLES}</style>
      <div class="field" role="search">
        <img class="codey" alt="" src="${escape(codey)}" />
        <input type="search" aria-label="Ask Codey" placeholder="Ask Codey…" autocomplete="off" spellcheck="false" aria-controls="ask-codey-panel" aria-expanded="false" />
        <kbd aria-hidden="true">/</kbd>
      </div>
      <div class="panel" id="ask-codey-panel" data-panel hidden></div>`;
    this.input = this.root.querySelector('input')!;
    this.panel = this.root.querySelector('[data-panel]')!;
    this.input.addEventListener('input', () => this.typed());
    this.root.querySelector('.field')!.addEventListener('click', () => this.input.focus());   // on a phone, Codey's face opens it
    this.input.addEventListener('focus', () => { if (this.panel.childElementCount) this.open(); });
    this.root.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Escape') { this.close(); this.input.blur(); } });
    this.panel.addEventListener('click', (e) => this.follow(e));
  }

  private typed(): void {
    if (this.timer) clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.asked++;
      this.close();
      return;
    }
    this.timer = setTimeout(() => void this.find(q), AskCodey.debounceMs);
  }

  private async find(q: string): Promise<void> {
    const mine = ++this.asked;
    this.panel.innerHTML = `<p class="status" data-status>Codey is looking…</p>`;
    this.open();
    const found = await search(q, this.fetcher, AskCodey.limit);
    if (mine !== this.asked) return;   // a newer search has started
    this.show(q, found);
  }

  private show(q: string, found: Found): void {
    const parts: string[] = [];
    if (found.guided) {
      const g = found.guided;
      parts.push(`<section class="guided" data-guided><p class="label">Codey says</p><p><b>${escape(g.name)}</b>: ${escape(g.excerpt)}</p>${g.url ? `<a href="${escape(g.url)}">Read ${escape(g.name)}</a>` : ''}</section>`);
    }
    for (const group of found.groups) {
      parts.push(`<section data-group="${group.kind}"><p class="label">${escape(group.label)}</p><ul>${group.hits.map((h) =>
        `<li><a href="${escape(h.url)}"><span class="title">${escape(h.title)}</span>${h.meta ? `<span class="meta">${escape(h.meta)}</span>` : ''}${h.text ? `<span class="text">${escape(h.text)}</span>` : ''}</a></li>`).join('')}</ul></section>`);
    }
    if (!found.groups.length && !found.guided) parts.push(`<p class="status" data-none>Nothing found for “${escape(q)}”. Try fewer or other words.</p>`);
    if (!this.hasAttribute('signed-in')) {
      parts.push(`<p class="register" data-register>Registration is free: <a href="/accounts/register">register</a> to track your stats and progress.</p>`);
    }
    this.panel.innerHTML = parts.join('');
    this.open();
  }

  /** A result clicked: let the page route it if it wants to. */
  private follow(e: Event): void {
    const link = (e.target as HTMLElement).closest('a');
    if (!link) return;
    const url = link.getAttribute('href')!;
    const navigate = new CustomEvent('ask-codey-navigate', { detail: { url }, bubbles: true, composed: true, cancelable: true });
    this.close();
    if (!this.dispatchEvent(navigate)) e.preventDefault();
  }

  private open(): void {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  private close(): void {
    if (!this.panel) return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

function escape(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

const STYLES = `
  :host { position: relative; display: inline-block; font-family: var(--font-body, system-ui, sans-serif); color: var(--color-fg, #1f2430); }
  .field { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid var(--color-divider, #d5dbe1); border-radius: 999px; padding: 3px 10px 3px 4px; min-width: 220px; }
  .field:focus-within { outline: 2px solid var(--color-accent, #c0562c); outline-offset: 1px; }
  .codey { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; background: var(--color-yellow, #f5c542); }
  input { flex: 1; min-width: 0; border: none; outline: none; font: inherit; font-size: 14px; background: transparent; color: inherit; }
  input::-webkit-search-cancel-button { cursor: pointer; }
  kbd { font: 12px var(--font-mono, ui-monospace, monospace); color: #6b7585; border: 1px solid #d5dbe1; border-radius: 4px; padding: 0 5px; }
  .field:focus-within kbd { display: none; }
  .panel { position: absolute; right: 0; top: calc(100% + 6px); z-index: 1000; width: min(420px, calc(100vw - 32px)); max-height: min(70vh, 560px); overflow: auto;
    background: #fff; border: 1px solid var(--color-divider, #d5dbe1); box-shadow: 0 12px 32px rgba(20, 30, 50, 0.18); padding: 10px 0; text-align: left; }
  .panel[hidden] { display: none; }
  section { padding: 4px 14px 8px; }
  .label { margin: 4px 0; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-accent, #c0562c); }
  ul { list-style: none; margin: 0; padding: 0; }
  li a { display: grid; grid-template-columns: 1fr auto; gap: 0 8px; padding: 6px 8px; margin: 0 -8px; color: inherit; text-decoration: none; border-radius: 6px; }
  li a:hover, li a:focus-visible { background: var(--color-neutral-100, #f1f4f7); outline: none; }
  .title { font-weight: 600; }
  .meta { font-size: 12px; color: #6b7585; }
  .text { grid-column: 1 / -1; font-size: 13px; color: #4a5468; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .guided { background: #fff8e6; border-left: 3px solid var(--color-yellow, #f5c542); margin: 0 10px 8px; padding: 8px 10px; }
  .guided p { margin: 2px 0; font-size: 14px; }
  .guided a, .register a { color: var(--color-accent, #c0562c); font-weight: 600; }
  .status, .register { margin: 6px 14px; font-size: 14px; color: #4a5468; }
  .register { border-top: 1px solid var(--color-divider, #d5dbe1); padding-top: 8px; }
  @media (max-width: 640px) {
    /* Closed: Codey's face only. Open: the field lies over the header and the panel under it, the page never widens. */
    .field { min-width: 0; width: 36px; height: 36px; box-sizing: border-box; padding: 3px; border-radius: 50%; }
    .field kbd { display: none; }
    .field input { flex: 0 0 0; width: 0; opacity: 0; }   /* still focusable: "/" and a tap on Codey open it */
    .field:focus-within { position: fixed; left: 16px; right: 16px; top: 12px; width: auto; height: auto; z-index: 1001; border-radius: 999px; padding: 3px 10px 3px 4px; }
    .field:focus-within input { flex: 1; width: auto; opacity: 1; }
    .panel { position: fixed; left: 16px; right: 16px; top: 60px; width: auto; }
  }
`;

if (!customElements.get('ask-codey')) customElements.define('ask-codey', AskCodey);
